import type { ColumnDef } from '@tanstack/react-table';
import { Eye, Pencil } from 'lucide-react';
import * as React from 'react';
import {
  Button,
  DataTable,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  StatusBadge,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@payment-plan/ui';
import { CompanyFormDialog } from './CompanyFormDialog';
import { CompanyLogo } from './CompanyLogo';
import { useCompanies, useToggleCompany } from './use-companies';
import type { Company } from '../../../types/admin';
import { textoSegunAcceso, useAccesoAdmin } from '../acceso-admin';

const PAGE_SIZE = 20;

type EstadoFilter = 'TODAS' | 'ACTIVA' | 'INACTIVA';

export function CompaniesPage(): React.JSX.Element {
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState('');
  const [estado, setEstado] = React.useState<EstadoFilter>('TODAS');
  const [formOpen, setFormOpen] = React.useState(false);
  const [mode, setMode] = React.useState<'view' | 'edit'>('edit');
  const [editing, setEditing] = React.useState<Company | null>(null);

  const { data, isLoading, isError, refetch } = useCompanies(page, PAGE_SIZE);
  const toggleCompany = useToggleCompany();

  const acceso = useAccesoAdmin('inmobiliaria');
  const canCreate = acceso.crear;
  const canEdit = acceso.editar;

  const rows = React.useMemo(() => {
    const list = data?.data ?? [];
    const term = search.trim().toLowerCase();
    return list.filter((company) => {
      const matchesTerm =
        !term ||
        company.nombre.toLowerCase().includes(term) ||
        company.nit.toLowerCase().includes(term) ||
        company.codigo.toLowerCase().includes(term);
      const matchesEstado =
        estado === 'TODAS' || (estado === 'ACTIVA' ? company.activa : !company.activa);
      return matchesTerm && matchesEstado;
    });
  }, [data, search, estado]);

  const columns = React.useMemo<Array<ColumnDef<Company, unknown>>>(
    () => [
      {
        id: 'logo',
        header: 'Logo',
        cell: ({ row }) => <CompanyLogo company={row.original} />,
      },
      { accessorKey: 'codigo', header: 'Código' },
      { accessorKey: 'nombre', header: 'Nombre' },
      { accessorKey: 'nit', header: 'NIT' },
      { accessorKey: 'ciudad', header: 'Ciudad' },
      {
        id: 'estado',
        header: 'Estado',
        cell: ({ row }) =>
          canEdit ? (
            <Switch
              checked={row.original.activa}
              aria-label={`Estado de ${row.original.nombre}`}
              onCheckedChange={(value) =>
                toggleCompany.mutate({ id: row.original.id, activa: value })
              }
            />
          ) : (
            <StatusBadge status={row.original.activa ? 'ACTIVA' : 'INACTIVA'} />
          ),
      },
    ],
    [canEdit, toggleCompany],
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <PageHeader
          eyebrow="Administración"
          title="Inmobiliarias"
          description={textoSegunAcceso(acceso, {
            gestion: 'Administra las inmobiliarias del sistema.',
            consulta: 'Consulta los datos de tu inmobiliaria.',
          })}
          action={
            canCreate ? (
              <Button
                onClick={() => {
                  setEditing(null);
                  setMode('edit');
                  setFormOpen(true);
                }}
              >
                Nueva inmobiliaria
              </Button>
            ) : undefined
          }
        />

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Input
            aria-label="Buscar inmobiliaria"
            placeholder="Buscar por nombre o NIT"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="sm:max-w-xs"
          />
          <select
            aria-label="Estado"
            value={estado}
            onChange={(event) => setEstado(event.target.value as EstadoFilter)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="TODAS">Todas</option>
            <option value="ACTIVA">Activas</option>
            <option value="INACTIVA">Inactivas</option>
          </select>
        </div>

        {isLoading ? (
          <LoadingState message="Cargando inmobiliarias" />
        ) : isError ? (
          <ErrorState
            message="No fue posible cargar las inmobiliarias"
            onRetry={() => void refetch()}
          />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            emptyMessage="No hay inmobiliarias"
            manualPagination
            rowCount={data?.meta.total ?? 0}
            pagination={{ pageIndex: page - 1, pageSize: PAGE_SIZE }}
            onPaginationChange={(state) => setPage(state.pageIndex + 1)}
            rowActions={(company) => (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Ver ${company.nombre}`}
                      onClick={() => {
                        setEditing(company);
                        setMode('view');
                        setFormOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Ver</TooltipContent>
                </Tooltip>
                {canEdit && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Editar ${company.nombre}`}
                        onClick={() => {
                          setEditing(company);
                          setMode('edit');
                          setFormOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Editar</TooltipContent>
                  </Tooltip>
                )}
              </>
            )}
          />
        )}

        <CompanyFormDialog
          open={formOpen}
          onOpenChange={(open) => {
            setFormOpen(open);
            if (!open) {
              setEditing(null);
            }
          }}
          company={editing}
          mode={mode}
        />
      </div>
    </TooltipProvider>
  );
}
